/** All subscription plans the catalog distinguishes between. */
export const PLANS = ["free", "pro"] as const;

/** A subscription plan a business can be on. */
export type Plan = (typeof PLANS)[number];

/**
 * Plan a business is on until a subscription moves it off. New BusinessProfile
 * rows are created on this plan, and readers fall back to it when `plan` is
 * unset.
 */
export const DEFAULT_PLAN: Plan = "free";

/**
 * Catalog schema version. Bump when the shape of `FEATURES` changes in a way
 * that requires clients to refresh their cached JWT entitlement claim.
 */
export const CATALOG_VERSION = "v2";

/** Specification for a single gateable feature. */
export type FeatureSpec = {
  /** Plans on which this feature is enabled by default. */
  plans: readonly Plan[];
};

/**
 * The full catalog of gateable features. Keys are feature identifiers used
 * everywhere in app code; values declare which plans include each feature.
 *
 * Add a feature: append an entry here. Move between tiers: edit `plans`.
 * Remove a feature: delete the entry — TypeScript will surface every call site.
 */
export const FEATURES = {
  /**
   * Dual pricing / freepay at checkout: separate posted credit/debit prices to recover
   * card acceptance cost. Never rename to "fee"/"surcharge" — compliance hinges on it.
   */
  "payments.freepay": { plans: ["pro"] },
} as const satisfies Record<string, FeatureSpec>;

/** Union of every feature identifier in {@link FEATURES}. */
export type FeatureKey = keyof typeof FEATURES;

/** Specification for a single numeric limit. */
export type LimitSpec = {
  /** Ceiling on each plan. */
  byPlan: Readonly<Record<Plan, number>>;
  unit: "cents" | "count";
};

/**
 * The full catalog of plan-scoped numeric limits.
 *
 * The numeric counterpart to {@link FEATURES}: same plan-keyed resolution, same
 * per-business override mechanism, same delivery through the JWT. A limit is an
 * entitlement that happens to be a number, and keeping the two in one catalog is
 * what stops the numeric side growing its own resolver — which is what
 * `getBusinessPlan` became.
 *
 * **Plan is the only input.** Identity verification, underwriting, and any other
 * risk control are deliberately absent. The effective ceiling a merchant is held
 * to is `min(plan ceiling, risk ceiling)`, computed where the limit is enforced;
 * folding risk in here is how a compliance decision ends up being described as a
 * subscription tier.
 */
export const LIMITS = {
  /** Largest single transaction the merchant may take. */
  "payments.transaction_max": {
    byPlan: { free: 100_000, pro: 1_000_000 },
    unit: "cents",
  },
} as const satisfies Record<string, LimitSpec>;

/** Union of every limit identifier in {@link LIMITS}. */
export type LimitKey = keyof typeof LIMITS;
